import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { formatDisplayTime } from "../lib/display-time";
import {
  fetchFeedAckRoster,
  setFeedPostPinned,
  setFeedPostRequireAck,
  type FeedAckRosterEntry,
  type FeedPost,
} from "../data/feed";

// The console's Feed list: what was posted, to whom, and - for required posts - who has actually
// read it. Pin and require-ack are per-post toggles behind feed.manage; the ack roster (lock 2.8
// read receipts) is the supervisor's answer to "who still has not acknowledged this".

export function FeedPostList({
  client,
  accountId,
  posts,
  timeZone,
  canManage,
  onChanged,
  selectedPostId = null,
}: {
  readonly client: SupabaseClient;
  readonly accountId: string;
  readonly posts: readonly FeedPost[];
  readonly timeZone: string;
  readonly canManage: boolean;
  readonly onChanged: () => void;
  readonly selectedPostId?: string | null;
}) {
  if (posts.length === 0) {
    return (
      <p className="surface__state" role="status">
        Nothing has been posted to this account's Feed yet.
      </p>
    );
  }

  return (
    <ul className="feed-list" aria-label="Feed posts">
      {posts.map((post) => (
        <li key={post.postId}>
          <FeedPostRow
            client={client}
            accountId={accountId}
            post={post}
            timeZone={timeZone}
            canManage={canManage}
            onChanged={onChanged}
            selected={post.postId === selectedPostId}
          />
        </li>
      ))}
    </ul>
  );
}

function FeedPostRow({
  client,
  accountId,
  post,
  timeZone,
  canManage,
  onChanged,
  selected,
}: {
  readonly client: SupabaseClient;
  readonly accountId: string;
  readonly post: FeedPost;
  readonly timeZone: string;
  readonly canManage: boolean;
  readonly onChanged: () => void;
  readonly selected: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function toggle(action: "pin" | "require-ack"): Promise<void> {
    if (busy || !canManage) return;
    setBusy(true);
    setFailure(null);
    const outcome =
      action === "pin"
        ? await setFeedPostPinned(client, accountId, post.postId, !post.isPinned)
        : await setFeedPostRequireAck(client, accountId, post.postId, !post.requiresAck);
    setBusy(false);
    if (outcome.ok) {
      onChanged();
    } else {
      setFailure(outcome.reason);
    }
  }

  return (
    <article
      id={`feed-post-${post.postId}`}
      className={[
        "feed-post",
        post.isPinned ? "feed-post--pinned" : "",
        selected ? "feed-post--selected" : "",
      ].filter(Boolean).join(" ")}
      aria-current={selected ? "true" : undefined}
    >
      <header className="feed-post__head">
        <div className="feed-post__who">
          <span className="feed-post__author">{post.authorDisplayName}</span>
          {/* Plate 04: line two is always audience then timestamp. A reader should never wonder
              whether a post was meant for them. */}
          <span className="feed-post__meta">
            {post.audienceLabel} · {formatDisplayTime(post.publishedAt, timeZone)}
          </span>
        </div>
        <div className="feed-post__tags">
          {post.isPinned ? <span className="feed-post__tag feed-post__tag--pin">Pinned</span> : null}
          {post.requiresAck ? (
            <span className="feed-post__tag feed-post__tag--ack">Acknowledgement required</span>
          ) : null}
        </div>
      </header>

      <h3 className="feed-post__title">{post.title}</h3>
      <p className="feed-post__body">{post.body}</p>

      <footer className="feed-post__foot">
        <span className="feed-post__count">
          {post.ackCount === 1 ? "1 acknowledged" : `${post.ackCount} acknowledged`}
          {post.requiresAck && post.ackDueAt !== null
            ? ` · due ${formatDisplayTime(post.ackDueAt, timeZone)}`
            : ""}
        </span>
        {canManage ? (
          <div className="feed-post__actions">
            <button type="button" className="builder__navbtn" disabled={busy} onClick={() => void toggle("pin")}>
              {post.isPinned ? "Unpin" : "Pin"}
            </button>
            <button
              type="button"
              className="builder__navbtn"
              disabled={busy}
              onClick={() => void toggle("require-ack")}
            >
              {post.requiresAck ? "Stop requiring ack" : "Require ack"}
            </button>
          </div>
        ) : null}
      </footer>

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}

      {canManage && post.requiresAck ? (
        <FeedAckRoster
          client={client}
          accountId={accountId}
          postId={post.postId}
          title={post.title}
          timeZone={timeZone}
        />
      ) : null}
    </article>
  );
}

/**
 * Read receipts for one post (lock 2.8). Loaded on demand: a roster is a per-post question, and
 * fetching every roster up front would pull the whole roster of the account into the page for no
 * reason. Outstanding names come first because that is the only actionable half.
 */
function FeedAckRoster({
  client,
  accountId,
  postId,
  title,
  timeZone,
}: {
  readonly client: SupabaseClient;
  readonly accountId: string;
  readonly postId: string;
  readonly title: string;
  readonly timeZone: string;
}) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<readonly FeedAckRosterEntry[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function load(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      setEntries(await fetchFeedAckRoster(client, accountId, postId));
    } catch (caught) {
      setFailure(caught instanceof Error ? caught.message : "Could not load who has acknowledged this post.");
    } finally {
      setBusy(false);
    }
  }

  const outstanding = entries?.filter((entry) => entry.ackedAt === null) ?? [];

  return (
    <div className="feed-post__roster">
      <button
        type="button"
        className="builder__navbtn"
        aria-expanded={open}
        disabled={busy}
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next && entries === null) void load();
        }}
      >
        {busy ? "Loading..." : open ? "Hide who acknowledged" : "Who acknowledged?"}
      </button>

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}

      {open && entries !== null ? (
        entries.length === 0 ? (
          <p className="surface__state" role="status">
            Nobody is in this post's audience yet.
          </p>
        ) : (
          <>
            <p className="feed-post__roster-summary">
              {outstanding.length === 0
                ? "Everyone in the audience has acknowledged."
                : `${outstanding.length} of ${entries.length} still outstanding.`}
            </p>
            <table className="commercial__table" aria-label={`Acknowledgements for ${title}`}>
              <thead>
                <tr>
                  <th scope="col">Member</th>
                  <th scope="col">Role</th>
                  <th scope="col">Acknowledged</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={`${entry.memberDisplayName}:${entry.role}`}>
                    <td>{entry.memberDisplayName}</td>
                    <td>{entry.role}</td>
                    <td>
                      {entry.ackedAt === null ? (
                        <span className="feed-post__outstanding">Outstanding</span>
                      ) : (
                        formatDisplayTime(entry.ackedAt, timeZone)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )
      ) : null}
    </div>
  );
}
